import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { useDebugMode } from './stores/debug-store';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Helper to get a random item from an array
export const getRandomItem = (array: string[]) => {
  const randomIndex = Math.floor(Math.random() * array.length);
  return array[randomIndex];
};

// Hook to check if debug mode is enabled
export const useIsDebugMode = () => {
  return useDebugMode();
};

/**
 * Sanitizes and preprocesses QTI XML content to prevent parsing errors and preserve formatting.
 *
 * This function:
 * - Converts unescaped form feed characters to `\f`
 * - Escapes raw `<` and `>` characters only inside text nodes
 * - Normalizes non-breaking spaces
 *
 * @param xml - The XML string to sanitize
 * @returns Sanitized XML string safe for DOMParser processing
 */
export const sanitizeXml = (xml: string): string => {
  if (!xml) return xml;

  const parser = new DOMParser();
  let processedXml = xml.replace(/(?<!\\)\f/g, '\\f');

  try {
    const doc = parser.parseFromString(processedXml, 'application/xml');

    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      console.warn('Fallback due to parser error:', parserError.textContent);
      return processedXml
        .replace(/&gt;/g, '&amp;gt;')
        .replace(/&lt;/g, '&amp;lt;')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;nbsp;/g, ' ');
    }

    // Only escape < and > in raw text nodes—not tags
    const walker = document.createTreeWalker(doc, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const node = walker.currentNode!;
      if (node.nodeValue?.includes('<') || node.nodeValue?.includes('>')) {
        node.nodeValue = node.nodeValue
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      }
    }

    processedXml = new XMLSerializer().serializeToString(doc.documentElement);
  } catch (error) {
    console.error('Error parsing XML:', error);
  }

  return processedXml.replace(/&nbsp;/g, ' ').replace(/&amp;nbsp;/g, ' ');
};

export const sortBySortOrder = <T extends { sortOrder?: number }>(
  a: T,
  b: T
) => {
  const sortOrderA = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
  const sortOrderB = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
  return sortOrderA - sortOrderB;
};
